import React, { useCallback } from 'react';
import BlockCanvas from '../../BlockCanvas';
import { useConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  Block,
  InstructionListBlockData,
  InstructionRow,
  INSTRUCTION_BLOCK_TYPES,
  RowFragment,
  createBlock,
  generateBlockId,
  instructionRowBlocks,
  instructionRowIsEmpty,
} from '../../../config';
import './instruction-list-block.scss';

interface InstructionListBlockProps {
  block: Block;
  onChange: (data: InstructionListBlockData) => void;
  isEditing: boolean;
  /**
   * When the list spans more than one page, the items this page shows. Absent
   * means the whole list (the measurement copy, and any list that fits).
   */
  fragment?: RowFragment;
}

/**
 * The numbered body of a site instruction: REF | DESCRIPTION items sitting
 * under the table's header row.
 *
 * Each item holds ordinary document blocks -- paragraphs, bullet lists and
 * headings -- rendered by a nested BlockCanvas. That is what gives an
 * instruction the report's editing behaviour (slash menu, inline formatting,
 * Enter for a new paragraph) without a second implementation: BlockCanvas
 * scopes every DOM query to its own ref, so one per item does not interfere
 * with its neighbours.
 *
 * The REF ("1.0", "2.0", ...) is derived from position and never stored, so
 * moving or deleting an item renumbers the rest rather than leaving a gap.
 *
 * Two things here exist for editor/PDF parity, not for the UI:
 *
 * 1. Empty items are rendered, not skipped -- and DocumentPDF renders them too.
 *    The editor measures this block to decide where the page breaks; anything
 *    one surface shows and the other hides makes that measurement a lie.
 * 2. `data-instruction-row` marks each item so DocumentEditor can measure items
 *    individually and break the list between them across pages.
 */
const InstructionListBlock: React.FC<InstructionListBlockProps> = ({
  block,
  onChange,
  isEditing,
  fragment,
}) => {
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const data = block.data as InstructionListBlockData;
  const allRows = data.rows ?? [];

  // Indices are against the WHOLE list, so an item keeps its REF number and
  // its move/remove actions still address the right item on a continuation
  // page. Only the slice is rendered.
  const firstIndex = fragment ? fragment.rowStart : 0;
  const rows = fragment ? allRows.slice(fragment.rowStart, fragment.rowEnd) : allRows;

  const setRows = useCallback(
    (next: InstructionRow[]) => onChange({ ...data, rows: next }),
    [data, onChange]
  );

  const setRowBlocks = (rowId: string, blocks: Block[]) =>
    setRows(allRows.map(r => (r.id === rowId ? { id: r.id, blocks } : r)));

  const addRowAt = (index: number) => {
    const next = [...allRows];
    next.splice(index, 0, { id: generateBlockId(), blocks: [createBlock('paragraph')] });
    setRows(next);
    focusRow(index);
  };

  const removeRow = async (index: number) => {
    // Always leave one item. An instruction with none collapses to nothing and
    // there would be no way to click back into it.
    if (allRows.length <= 1) return;

    // An empty item is nothing to lose, so it just goes. One with content is
    // asked about: the × sits beside the move arrows, and a misclick would
    // otherwise discard typed text with no way back.
    if (!instructionRowIsEmpty(allRows[index])) {
      const proceed = await confirm({
        title: `Remove item ${index + 1}.0?`,
        message: <p>Its content will be removed from the instruction.</p>,
        confirmLabel: 'Remove',
        tone: 'danger',
      });
      if (!proceed) return;
    }

    setRows(allRows.filter((_, i) => i !== index));
  };

  const moveRow = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= allRows.length) return;
    const next = [...allRows];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
  };

  /** Move the caret into the first editable of an item, by position. */
  const focusRow = (index: number) => {
    window.requestAnimationFrame(() => {
      const el = window.document.querySelector(
        `.editor-main [data-block-id="${block.id}"] [data-instruction-row-index="${index}"] [contenteditable="true"]`
      ) as HTMLElement | null;
      el?.focus();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    // Tab moves between items. Enter belongs to the item's own content, where
    // it makes a new paragraph like anywhere else in the builder.
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const target = e.shiftKey ? index - 1 : index + 1;
    if (target < 0) return;
    if (target >= allRows.length) {
      if (e.shiftKey) return;
      addRowAt(allRows.length);
      return;
    }
    focusRow(target);
  };

  return (
    <div className="instruction-list-block">
      {confirmDialog}

      {/* Part of the table, not of the page chrome, so a list continuing onto
          another page carries its column headings with it. */}
      <div className="si-table-head" contentEditable={false}>
        <span className="si-table-head__ref">REF</span>
        <span className="si-table-head__description">DESCRIPTION</span>
      </div>

      {rows.map((row, offset) => {
        const index = firstIndex + offset;
        return (
        <div
          key={row.id}
          className="instruction-row"
          data-instruction-row={row.id}
          data-instruction-row-index={index}
          onKeyDown={e => handleKeyDown(e, index)}
        >
          {isEditing && (
            <div className="instruction-row__add" contentEditable={false}>
              <button
                type="button"
                title="Add an item above"
                aria-label={`Add an item above item ${index + 1}`}
                onClick={() => addRowAt(index)}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <path d="M8 2v5M5.5 4.5h5" />
                  <path d="M3 11.5h10" />
                </svg>
              </button>
              <button
                type="button"
                title="Add an item below"
                aria-label={`Add an item below item ${index + 1}`}
                onClick={() => addRowAt(index + 1)}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <path d="M3 4.5h10" />
                  <path d="M8 9v5M5.5 11.5h5" />
                </svg>
              </button>
            </div>
          )}

          <div className="instruction-row__ref">{index + 1}.0</div>

          <div className="instruction-row__content">
            <BlockCanvas
              blocks={instructionRowBlocks(row)}
              onChange={blocks => setRowBlocks(row.id, blocks)}
              isEditing={isEditing}
              allowedBlockTypes={INSTRUCTION_BLOCK_TYPES}
            />
          </div>

          {isEditing && (
            <div className="instruction-row__actions" contentEditable={false}>
              <button
                type="button"
                title="Move up"
                aria-label={`Move item ${index + 1} up`}
                disabled={index === 0}
                onClick={() => moveRow(index, -1)}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 12.5V3.5M4.5 7L8 3.5 11.5 7" />
                </svg>
              </button>
              <button
                type="button"
                title="Move down"
                aria-label={`Move item ${index + 1} down`}
                disabled={index === allRows.length - 1}
                onClick={() => moveRow(index, 1)}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3.5v9M4.5 9L8 12.5 11.5 9" />
                </svg>
              </button>
              <button
                type="button"
                title="Remove this item"
                aria-label={`Remove item ${index + 1}`}
                disabled={allRows.length <= 1}
                onClick={() => { void removeRow(index); }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
              </button>

            </div>
          )}
        </div>
        );
      })}

    </div>
  );
};

export default InstructionListBlock;
